/**
 * useDocumentProcessing Hook
 *
 * React hook for WebSocket-based real-time document processing updates.
 * Connects to the MHR WebSocket server and receives progress updates.
 *
 * Handles race conditions by:
 * - Tracking current documentId in a ref and validating messages
 * - Resetting reconnect attempts when documentId changes
 * - Using connection timeout to prevent indefinite hangs
 * - Cleanup flag to prevent stale updates after unmount/documentId change
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../services/supabase';

// WebSocket URL - use window.location to determine protocol
const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = process.env.REACT_APP_API_URL
    ? new URL(process.env.REACT_APP_API_URL).host
    : window.location.host;
  return `${protocol}//${host}`;
};

// Connection timeout in milliseconds
const CONNECTION_TIMEOUT = 30000;

export interface LayerStatus {
  status: 'pending' | 'processing' | 'complete';
  progress: number;
  pagesProcessed: number;
}

export interface ProcessingProgress {
  type: 'progress' | 'complete' | 'error' | 'connected' | 'subscribed' | 'close' | 'pong';
  jobId?: string;
  documentId: string;
  state?: string;
  progress: number;
  pagesProcessed: number;
  totalPages: number;
  currentLayer: 1 | 2 | 3;
  layerProgress: {
    layer1: LayerStatus;
    layer2: LayerStatus;
    layer3: LayerStatus;
  };
  message: string;
  estimatedTimeRemaining?: number; // seconds
  costs?: {
    extraction: number;
    ocr: number;
    vision: number;
    total: number;
  };
  error?: string;
  timestamp?: string;
  reason?: string; // For close messages
  // Summary generation status
  summaryStatus?: 'pending' | 'generating' | 'generated' | 'failed';
  summaryId?: string;
}

export interface UseDocumentProcessingOptions {
  autoReconnect?: boolean;
  reconnectDelay?: number; // Initial delay for reconnection (default: 2000ms)
  maxReconnectDelay?: number; // Maximum delay for exponential backoff (default: 30000ms)
  maxReconnectAttempts?: number;
  connectionTimeout?: number;
  onProgress?: (progress: ProcessingProgress) => void;
  onComplete?: (result: ProcessingProgress) => void;
  onError?: (error: string) => void;
}

export interface UseDocumentProcessingResult {
  progress: ProcessingProgress | null;
  isConnected: boolean;
  isConnecting: boolean;
  isComplete: boolean;
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  subscribe: (documentId: string) => void;
}

const DEFAULT_PROGRESS: Omit<ProcessingProgress, 'documentId'> = {
  type: 'progress',
  progress: 0,
  pagesProcessed: 0,
  totalPages: 0,
  currentLayer: 1,
  layerProgress: {
    layer1: { status: 'pending', progress: 0, pagesProcessed: 0 },
    layer2: { status: 'pending', progress: 0, pagesProcessed: 0 },
    layer3: { status: 'pending', progress: 0, pagesProcessed: 0 },
  },
  message: 'Waiting to start...',
};

export function useDocumentProcessing(
  documentId: string | null,
  options: UseDocumentProcessingOptions = {}
): UseDocumentProcessingResult {
  const {
    autoReconnect = true,
    reconnectDelay = 2000, // Start with 2s delay
    maxReconnectDelay = 30000, // Max 30s delay with exponential backoff
    maxReconnectAttempts = 5,
    connectionTimeout = CONNECTION_TIMEOUT,
    onProgress,
    onComplete,
    onError,
  } = options;

  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentDocumentIdRef = useRef<string | null>(documentId);
  // Cleanup flag to prevent stale updates after unmount or documentId change
  const cleanupFlagRef = useRef(false);
  // Monotonic per-attempt token. connect() now creates the socket AFTER an
  // awaited token fetch, so a superseding connect() must be able to invalidate
  // an in-flight one (a boolean can't distinguish "superseded" from "current").
  const connectGenRef = useRef(0);

  // Update the current document ID ref when it changes and reset reconnect attempts
  useEffect(() => {
    currentDocumentIdRef.current = documentId;
    // Reset reconnect attempts when documentId changes to give fresh connection a fair chance
    reconnectAttemptsRef.current = 0;
  }, [documentId]);

  const disconnect = useCallback(() => {
    // Set cleanup flag to prevent any pending callbacks from updating state
    cleanupFlagRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const connect = useCallback(() => {
    const targetDocumentId = currentDocumentIdRef.current;
    if (!targetDocumentId) {
      return;
    }

    // Reset cleanup flag for new connection
    cleanupFlagRef.current = false;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Clear any existing connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    setIsConnecting(true);
    setError(null);

    // Claim this connect attempt. A later connect() bumps the generation so an
    // in-flight (awaiting) attempt can detect it was superseded and bail before
    // it creates an orphan socket / timer.
    const myGen = ++connectGenRef.current;

    // The browser WebSocket API can't set headers, so we fetch the Supabase
    // access token and pass it as a subprotocol value ('bearer.<jwt>') that the
    // server's upgrade handler reads. Wrapped in an async IIFE so connect() stays
    // synchronous for its callers while awaiting the session.
    (async () => {
      let token: string | undefined;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        token = session?.access_token;
      } catch (err) {
        console.error('[useDocumentProcessing] Failed to read session:', err);
      }

      // Abort if a newer connect() superseded us, or cleanup/unmount happened
      // while awaiting the token — otherwise we'd orphan a socket and its timer.
      if (myGen !== connectGenRef.current || cleanupFlagRef.current) {
        return;
      }

      // No token → surface an auth error rather than storming reconnects.
      if (!token) {
        setError('Not authenticated');
        setIsConnecting(false);
        return;
      }

    try {
      const wsUrl = `${getWebSocketUrl()}/ws/mhr?documentId=${targetDocumentId}`;
      console.log('[useDocumentProcessing] Connecting to:', wsUrl);

      // Tracks whether THIS socket ever completed the WS handshake. A
      // pre-handshake auth rejection (server 401 during upgrade) closes as
      // code 1006 with no prior 'open' — handled distinctly in onclose.
      let socketOpened = false;

      const ws = new WebSocket(wsUrl, ['mhr.v1', `bearer.${token}`]);
      wsRef.current = ws;

      // Set up connection timeout - if we don't connect within the timeout, close and retry
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('[useDocumentProcessing] Connection timeout - closing and retrying');
          ws.close();
          // The onclose handler will trigger reconnect if autoReconnect is enabled
        }
      }, connectionTimeout);

      ws.onopen = () => {
        socketOpened = true;
        // Check cleanup flag before updating state
        if (cleanupFlagRef.current) {
          console.log('[useDocumentProcessing] Connection opened but cleanup was requested, closing');
          ws.close();
          return;
        }

        // Clear connection timeout on successful connection
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        console.log('[useDocumentProcessing] Connected');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        // Check cleanup flag before processing messages
        if (cleanupFlagRef.current) {
          console.log('[useDocumentProcessing] Ignoring message - cleanup was requested');
          return;
        }

        try {
          const data = JSON.parse(event.data) as ProcessingProgress;
          console.log('[useDocumentProcessing] Message:', data.type, data);

          // Validate that the message is for the current documentId
          // Some message types (connected, pong) may not have documentId, so we allow those through
          if (data.documentId && data.documentId !== currentDocumentIdRef.current) {
            console.log('[useDocumentProcessing] Ignoring message for old document:', data.documentId, 'current:', currentDocumentIdRef.current);
            return;
          }

          switch (data.type) {
            case 'connected':
              // Initial connection confirmation
              setProgress({
                ...DEFAULT_PROGRESS,
                documentId: targetDocumentId,
                message: 'Connected. Waiting for updates...',
              });
              break;

            case 'progress':
              setProgress(data);
              onProgress?.(data);
              break;

            case 'complete':
              setProgress(data);
              setIsComplete(true);
              onComplete?.(data);
              break;

            case 'error':
              setError(data.error || 'Processing error');
              setProgress(prev => prev ? { ...prev, type: 'error', error: data.error } : null);
              onError?.(data.error || 'Processing error');
              break;

            case 'subscribed':
              console.log('[useDocumentProcessing] Subscribed to document:', data.documentId);
              break;

            default:
              console.log('[useDocumentProcessing] Unknown message type:', data.type);
          }
        } catch (err) {
          console.error('[useDocumentProcessing] Error parsing message:', err);
        }
      };

      ws.onerror = (event) => {
        // Check cleanup flag before updating state
        if (cleanupFlagRef.current) {
          return;
        }
        console.error('[useDocumentProcessing] WebSocket error:', event);
        setError('Connection error');
      };

      ws.onclose = (event) => {
        console.log('[useDocumentProcessing] Connection closed:', event.code, event.reason);

        // Clear connection timeout if still pending
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }

        // Check cleanup flag before updating state or reconnecting
        if (cleanupFlagRef.current) {
          console.log('[useDocumentProcessing] Connection closed after cleanup requested, not reconnecting');
          wsRef.current = null;
          return;
        }

        setIsConnected(false);
        setIsConnecting(false);
        wsRef.current = null;

        // Server auth/authz denials (4001 unauthenticated, 4003 access denied)
        // are non-retryable — surface a clear error instead of storming reconnects.
        if (event.code === 4001 || event.code === 4003) {
          setError(
            event.code === 4001
              ? 'Not authenticated'
              : 'You do not have access to this document'
          );
          return;
        }

        // A failed UPGRADE (e.g. expired/revoked token → server 401 before the
        // handshake completes) surfaces as a 1006 close with no prior 'open'.
        // Refresh the session so the bounded reconnect below retries with a
        // fresh token instead of re-sending the same stale one.
        if (!socketOpened && event.code !== 1000) {
          supabase.auth.refreshSession().catch(() => {});
        }

        // Auto-reconnect if enabled and not a clean close
        if (autoReconnect && event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          // Calculate delay with exponential backoff: delay * 2^(attempt-1), capped at maxReconnectDelay
          const exponentialDelay = Math.min(
            reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1),
            maxReconnectDelay
          );
          console.log(`[useDocumentProcessing] Reconnecting in ${exponentialDelay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            // Double-check cleanup flag before reconnecting
            if (cleanupFlagRef.current) {
              console.log('[useDocumentProcessing] Skipping reconnect - cleanup was requested');
              return;
            }
            if (currentDocumentIdRef.current) {
              connect();
            }
          }, exponentialDelay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError(
            socketOpened
              ? 'Unable to connect. Please check your network and try again.'
              : 'Unable to connect — your session may have expired. Please sign in again.'
          );
        }
      };
    } catch (err) {
      console.error('[useDocumentProcessing] Connection error:', err);
      setError('Failed to connect');
      setIsConnecting(false);
    }
    })();
  }, [autoReconnect, reconnectDelay, maxReconnectAttempts, connectionTimeout, onProgress, onComplete, onError]);

  const subscribe = useCallback((newDocumentId: string) => {
    // Reset reconnect attempts for new document
    reconnectAttemptsRef.current = 0;
    // Reset cleanup flag since we're actively subscribing
    cleanupFlagRef.current = false;

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Connect with the new document ID
      currentDocumentIdRef.current = newDocumentId;
      connect();
      return;
    }

    // Send subscribe message to switch documents
    wsRef.current.send(JSON.stringify({
      type: 'subscribe',
      documentId: newDocumentId,
    }));

    currentDocumentIdRef.current = newDocumentId;
    setIsComplete(false);
    setProgress({
      ...DEFAULT_PROGRESS,
      documentId: newDocumentId,
      message: 'Switched to new document...',
    });
  }, [connect]);

  // Connect when documentId changes
  useEffect(() => {
    if (documentId) {
      // Reset cleanup flag for new connection attempt
      cleanupFlagRef.current = false;
      setIsComplete(false);
      connect();
    } else {
      disconnect();
      setProgress(null);
      setIsComplete(false);
    }

    return () => {
      // Set cleanup flag before disconnecting to prevent race conditions
      cleanupFlagRef.current = true;
      disconnect();
    };
  }, [documentId, connect, disconnect]);

  // Heartbeat to keep connection alive
  useEffect(() => {
    if (!isConnected || !wsRef.current) {
      return;
    }

    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [isConnected]);

  return {
    progress,
    isConnected,
    isConnecting,
    isComplete,
    error,
    connect,
    disconnect,
    subscribe,
  };
}

export default useDocumentProcessing;
